import React, { useState, useEffect, FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { User, getCurrentUser, updateUserProfile } from '../../services/userService'; // Assuming updateUserProfile will be added
import { professionOptions, experienceOptions, SelectOption } from '../../utils/options'; // Assuming options are defined here
import { geocodeAndOffsetLocation } from '../../utils/geocoding'; // Import geocoding util
import { Alert } from 'react-bootstrap'; // Import Alert for geocoding errors
import { useQueryClient } from 'react-query'; // Import useQueryClient

// Define the shape of the form data
interface EditProfileFormData {
    name: string;
    profession: string;
    experience: string;
    company: string;
    linkedin: string;
    country: string;
    city: string;
    zipcode: string;
    skills: string[];
    // email is read-only, not part of editable form data
}

interface InitialLocation {
    city: string;
    country: string;
    zipcode: string;
}

const EditProfilePage: React.FC = () => {
    const { user: authUser, refreshUserData } = useAuth(); // Get authenticated user context (if needed) and refreshUserData
    const navigate = useNavigate();
    const queryClient = useQueryClient(); // Get query client instance
    const [formData, setFormData] = useState<EditProfileFormData>({
        name: '',
        profession: '',
        experience: '',
        company: '',
        linkedin: '',
        country: '',
        city: '',
        zipcode: '',
        skills: [''], // Start with one empty skill input
    });
    const [initialEmail, setInitialEmail] = useState<string>('');
    const [initialLocation, setInitialLocation] = useState<InitialLocation>({ city: '', country: '', zipcode: '' }); // State for initial location
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [isGeocoding, setIsGeocoding] = useState<boolean>(false); // Separate loading state for geocoding
    const [error, setError] = useState<string | null>(null);
    const [geocodingError, setGeocodingError] = useState<string | null>(null); // Specific error state for geocoding
    const [successMessage, setSuccessMessage] = useState<string | null>(null);

    useEffect(() => {
        const loadUserProfile = async () => {
            setIsLoading(true);
            setError(null);
            setSuccessMessage(null);
            try {
                const currentUser = await getCurrentUser();
                setFormData({
                    name: currentUser.name || '',
                    profession: currentUser.profession || '',
                    experience: currentUser.experience || '',
                    company: currentUser.company || '',
                    linkedin: currentUser.linkedin || '',
                    country: currentUser.country || '',
                    city: currentUser.city || '',
                    zipcode: currentUser.zipcode || '',
                    skills: currentUser.skills.length > 0 ? currentUser.skills : [''], // Ensure at least one skill input
                });
                setInitialEmail(currentUser.email || ''); // Store initial email
                // Store initial location for comparison
                setInitialLocation({
                    city: currentUser.city || '',
                    country: currentUser.country || '',
                    zipcode: currentUser.zipcode || ''
                });
            } catch (err: any) {
                console.error("Failed to fetch profile:", err);
                setError(err.message || 'Failed to load profile data for editing.');
            } finally {
                setIsLoading(false);
            }
        };

        loadUserProfile();
    }, []); // Fetch data on component mount

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value,
        }));
    };

    const handleSkillChange = (index: number, value: string) => {
        const newSkills = [...formData.skills];
        newSkills[index] = value;
        setFormData(prev => ({ ...prev, skills: newSkills }));
    };

    const addSkill = () => {
        setFormData(prev => ({ ...prev, skills: [...prev.skills, ''] }));
    };

    const removeSkill = (index: number) => {
        // Prevent removing the last skill input if it's the only one
        if (formData.skills.length <= 1) return;
        const newSkills = formData.skills.filter((_, i) => i !== index);
        setFormData(prev => ({ ...prev, skills: newSkills }));
    };


    const handleSubmit = async (e: FormEvent) => {
        e.preventDefault();
        setError(null);
        setSuccessMessage(null);
        setGeocodingError(null); // Clear previous geocoding errors
        setIsLoading(true); // Indicate general loading

        const skillsToSubmit = formData.skills.filter(skill => skill.trim() !== '');
        let geocodedCoords: { latitude: number; longitude: number } | null = null;

        // Check if location fields have changed
        const locationChanged = 
            formData.city !== initialLocation.city ||
            formData.country !== initialLocation.country ||
            formData.zipcode !== initialLocation.zipcode;

        if (locationChanged && formData.city && formData.country) { // Only geocode if changed and city/country are present
            setIsGeocoding(true); // Indicate geocoding specifically
            try {
                console.log("Location changed, attempting geocoding...");
                geocodedCoords = await geocodeAndOffsetLocation(
                    formData.city,
                    formData.country,
                    formData.zipcode
                );

                if (!geocodedCoords) {
                    // Handle geocoding failure - Stop submission
                    setGeocodingError("Could not verify the new location. Please check the city, country, and ZIP code.");
                    setIsLoading(false);
                    setIsGeocoding(false);
                    return; // Stop the submission
                }
                console.log("Geocoding successful:", geocodedCoords);
            } catch (error) {
                console.error("Geocoding error during profile update:", error);
                setGeocodingError("An error occurred while verifying the location.");
                setIsLoading(false);
                setIsGeocoding(false);
                return; // Stop the submission
            } finally {
                 setIsGeocoding(false); // Turn off geocoding indicator regardless of outcome
            }
        }

        // Prepare data for the backend
        const updatedData: Partial<User> & { latitude?: number | null, longitude?: number | null } = {
           ...formData,
           skills: skillsToSubmit,
        };

        // Add coordinates only if they were successfully geocoded
        if (geocodedCoords) {
            updatedData.latitude = geocodedCoords.latitude;
            updatedData.longitude = geocodedCoords.longitude;
        }

        console.log("Submitting updated profile data:", updatedData);

        try {
            const updatedUser = await updateUserProfile(updatedData);

            // --- Refresh Auth Context State --- 
            console.log("Profile update successful, refreshing auth context...");
            await refreshUserData(); // <<< Call the refresh function
            console.log("Auth context refreshed.");

            // --- Invalidate React Query Cache ---
            console.log("Invalidating relevant queries...");
            // Use invalidateQueries instead of resetQueries
            await queryClient.invalidateQueries('allUsers');
            await queryClient.invalidateQueries('userLocation');
            await queryClient.invalidateQueries(['user', 'me']); // Key used by other potential components
            console.log("Queries invalidated.");

            setSuccessMessage('Profile updated successfully!');
            // Give a moment for context/cache to potentially update before navigating
            setTimeout(() => navigate('/profile'), 500); // Reduced delay slightly
        } catch (err: any) {
            console.error("Profile update API call failed:", err);
            setError(err.message || 'Failed to update profile. Please try again.');
        } finally {
            setIsLoading(false); // Turn off general loading indicator
        }
    };

    if (isLoading && !formData.name) { // Show loading only initially
        return <div className="container py-5 text-center">Loading profile for editing...</div>;
    }

    return (
        <div className="container py-5">
            <div className="row">
                <div className="col-12 mb-4">
                    <div className="card border-0 shadow-sm">
                        <div className="card-header bg-white py-3 d-flex justify-content-between align-items-center">
                            <h5 className="mb-0 fw-bold">Edit Your Profile</h5>
                            <button onClick={() => navigate('/profile')} className="btn btn-sm btn-outline-primary">
                                <i className="fas fa-arrow-left me-1"></i> Back to Profile
                            </button>
                        </div>
                        <div className="card-body p-4">
                            {/* Display general errors */}
                            {error && <Alert variant="danger" className="mb-4">Error: {error}</Alert>}
                            {/* Display geocoding errors separately */}
                            {geocodingError && <Alert variant="warning" className="mb-4">{geocodingError}</Alert>}
                            {successMessage && <Alert variant="success" className="mb-4">{successMessage}</Alert>}

                            <form id="editProfileForm" onSubmit={handleSubmit}>
                                {/* Basic Information Section */}
                                <div className="mb-4">
                                    <h6 className="mb-3 text-primary">Basic Information</h6>
                                    <div className="row g-3">
                                        <div className="col-md-6">
                                            <label htmlFor="name" className="form-label">Full Name</label>
                                            <input
                                                type="text"
                                                className="form-control"
                                                id="name"
                                                name="name"
                                                value={formData.name}
                                                onChange={handleChange}
                                                required
                                                disabled={isLoading || isGeocoding}
                                            />
                                        </div>

                                        <div className="col-md-6">
                                            <label htmlFor="email" className="form-label">Email</label>
                                            <input
                                                type="email"
                                                className="form-control"
                                                id="email"
                                                name="email"
                                                value={initialEmail} // Use initial email state
                                                readOnly // Make it read-only
                                                disabled // Also disable interaction
                                            />
                                            <div className="form-text">Email cannot be changed</div>
                                        </div>

                                        <div className="col-md-6">
                                            <label htmlFor="profession" className="form-label">Profession</label>
                                            <select
                                                className="form-select"
                                                id="profession"
                                                name="profession"
                                                value={formData.profession}
                                                onChange={handleChange}
                                                required
                                                disabled={isLoading || isGeocoding}
                                            >
                                                {/* Populate with options - assumes professionOptions is imported */}
                                                <option value="">Select Profession...</option>
                                                {professionOptions.map((option: SelectOption) => (
                                                    <option key={option.value} value={option.value}>{option.label}</option>
                                                ))}
                                            </select>
                                        </div>

                                        <div className="col-md-6">
                                            <label htmlFor="experience" className="form-label">Experience Level</label>
                                            <select
                                                className="form-select"
                                                id="experience"
                                                name="experience"
                                                value={formData.experience}
                                                onChange={handleChange}
                                                required
                                                disabled={isLoading || isGeocoding}
                                            >
                                                 {/* Populate with options - assumes experienceOptions is imported */}
                                                <option value="">Select Experience...</option>
                                                 {experienceOptions.map((option: SelectOption) => (
                                                    <option key={option.value} value={option.value}>{option.label}</option>
                                                ))}
                                            </select>
                                        </div>

                                        <div className="col-md-6">
                                            <label htmlFor="company" className="form-label">Company</label>
                                            <input
                                                type="text"
                                                className="form-control"
                                                id="company"
                                                name="company"
                                                value={formData.company}
                                                onChange={handleChange}
                                                disabled={isLoading || isGeocoding}
                                            />
                                        </div>

                                        <div className="col-md-6">
                                            <label htmlFor="country" className="form-label">Country</label>
                                            <input
                                                type="text"
                                                className="form-control"
                                                id="country"
                                                name="country"
                                                value={formData.country}
                                                onChange={handleChange}
                                                disabled={isLoading || isGeocoding}
                                                // Consider adding validation or country select dropdown later
                                            />
                                        </div>

                                        <div className="col-md-6">
                                            <label htmlFor="city" className="form-label">City</label>
                                            <input
                                                type="text"
                                                className="form-control"
                                                id="city"
                                                name="city"
                                                value={formData.city}
                                                onChange={handleChange}
                                                disabled={isLoading || isGeocoding}
                                            />
                                        </div>

                                        <div className="col-md-6">
                                            <label htmlFor="zipcode" className="form-label">Zipcode</label>
                                            <input
                                                type="text"
                                                className="form-control"
                                                id="zipcode"
                                                name="zipcode"
                                                value={formData.zipcode}
                                                onChange={handleChange}
                                                disabled={isLoading || isGeocoding}
                                            />
                                        </div>
                                    </div>
                                </div>

                                {/* Social & Professional Links */}
                                <div className="mb-4">
                                    <h6 className="mb-3 text-primary">Professional Links</h6>
                                    <div className="mb-3">
                                        <label htmlFor="linkedin" className="form-label">LinkedIn Profile URL</label>
                                        <div className="input-group">
                                            {/* <span className="input-group-text"><i className="fab fa-linkedin-in"></i></span> Icon requires FontAwesome */}
                                            <span className="input-group-text">LI</span>
                                            <input
                                                type="url"
                                                className="form-control"
                                                id="linkedin"
                                                name="linkedin"
                                                value={formData.linkedin}
                                                onChange={handleChange}
                                                placeholder="https://linkedin.com/in/yourprofile"
                                                disabled={isLoading || isGeocoding}
                                            />
                                        </div>
                                    </div>
                                </div>

                                {/* Skills */}
                                <div className="mb-4">
                                    <h6 className="mb-3 text-primary">Skills</h6>
                                    <label className="form-label">Your Skills</label>
                                    <div id="skillsContainer" className="mb-3">
                                        {formData.skills.map((skill, index) => (
                                            <div key={index} className="d-flex align-items-center border rounded p-2 mb-2 skill-entry">
                                                <input
                                                    type="text"
                                                    className="form-control border-0 flex-grow-1"
                                                    placeholder="Enter a skill (e.g., Python, Data Analysis)"
                                                    value={skill}
                                                    onChange={(e) => handleSkillChange(index, e.target.value)}
                                                    disabled={isLoading || isGeocoding}
                                                />
                                                {formData.skills.length > 1 && ( // Show remove button only if more than one skill
                                                    <button
                                                        type="button"
                                                        className="btn btn-outline-danger btn-sm ms-2 remove-skill"
                                                        onClick={() => removeSkill(index)}
                                                        disabled={isLoading || isGeocoding}
                                                        title="Remove Skill"
                                                    >
                                                         {/* <i className="fas fa-times"></i> Icon requires FontAwesome */}
                                                         X
                                                    </button>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                    <button
                                        type="button"
                                        id="addSkillBtn"
                                        className="btn btn-sm btn-outline-secondary"
                                        onClick={addSkill}
                                        disabled={isLoading || isGeocoding}
                                    >
                                        {/* <i className="fas fa-plus me-1"></i> Icon requires FontAwesome */}
                                        + Add Another Skill
                                    </button>
                                </div>

                                {/* Omitted Sections: Networking Preferences, Location Preferences, Privacy */}

                                <div className="d-flex justify-content-between mt-4">
                                    <button type="button" onClick={() => navigate('/profile')} className="btn btn-outline-secondary" disabled={isLoading || isGeocoding}>
                                        Cancel
                                    </button>
                                    <button type="submit" className="btn btn-primary" disabled={isLoading || isGeocoding}>
                                        {isGeocoding ? 'Verifying Location...' : isLoading ? 'Saving...' : 'Save Changes'}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default EditProfilePage; 